import { useEffect, useRef, useState } from "react";
import { api } from "../api";

/**
 * Fotos und Urkunden hinzufuegen: hinziehen, einfuegen oder auswaehlen.
 *
 * Drei Wege, weil Leute drei verschiedene Sachen tun. Wer einen Ordner
 * offen hat, zieht; wer gerade gescannt oder einen Ausschnitt kopiert hat,
 * drueckt Strg+V; wer vom Handy kommt, tippt auf den Knopf. Der Knopf
 * allein war der Stand von vorher und der unbequemste der drei.
 *
 * Hochgeladen wird NACHEINANDER, nicht gleichzeitig - und zwar nicht aus
 * Hoeflichkeit, sondern weil `medien.sha256` einen eindeutigen Index hat und
 * die Route erst sucht und dann einfuegt. Zwei gleichzeitige Uploads
 * desselben Bildes liefen in eine Schluesselverletzung. Nebenbei ist die
 * Fortschrittsanzeige („3 von 7") so ueberhaupt erst moeglich.
 *
 * Ein Fehler bei einem Bild bricht den Stapel nicht ab - er wird gesammelt
 * und am Ende genannt. Wer sieben Bilder zieht und beim dritten einen
 * Tippfehler im Dateityp hat, will die anderen sechs trotzdem drin haben.
 */

/** Was am Ende eines Stapels herausgekommen ist. */
export interface Bilanz {
  /** Ids aller Bilder, die jetzt zugeordnet sind - auch der schon bekannten. */
  ids: number[];
  /** Wie viele davon schon in der Sammlung waren. */
  schonDa: number;
  /** Dateiname und Grund, je gescheiterter Datei. */
  fehler: string[];
}

const ERLAUBT = /^(image\/|application\/pdf$)/;
const ERLAUBT_ENDUNG = /\.(jpe?g|png|gif|webp|tiff?|heic|heif|bmp|pdf)$/i;

/** Manche Systeme liefern fuer HEIC oder TIFF keinen Mimetyp mit. */
const passt = (d: File) => ERLAUBT.test(d.type) || ERLAUBT_ENDUNG.test(d.name);

export function FotoAblage({
  zuordnen,
  fertig,
  aktiv = true,
}: {
  /**
   * Was mit einem hochgeladenen Bild geschehen soll - meist verknuepfen.
   * Der Rueckgabewert wird nicht gebraucht; `unknown` statt `void`, damit
   * eine API-Funktion direkt durchgereicht werden kann.
   */
  zuordnen: (medienId: number) => Promise<unknown>;
  /** Nach dem ganzen Stapel, einmal. */
  fertig: (bilanz: Bilanz) => void;
  aktiv?: boolean;
}) {
  const [ueber, setUeber] = useState(false);
  const [stand, setStand] = useState<{ fertig: number; gesamt: number } | null>(null);
  const feld = useRef<HTMLInputElement | null>(null);
  /**
   * `dragleave` feuert auch beim Wechsel auf ein Kindelement. Ohne Zaehler
   * flackert die Umrandung, sobald die Maus ueber den Text darin faehrt.
   */
  const tiefe = useRef(0);

  const laeuft = stand !== null;

  async function stapel(dateien: File[]) {
    if (!aktiv || laeuft) return;
    const gute = dateien.filter(passt);
    const bilanz: Bilanz = {
      ids: [],
      schonDa: 0,
      fehler: dateien
        .filter((d) => !passt(d))
        .map((d) => `${d.name}: kein Bild und kein PDF`),
    };
    if (gute.length === 0) {
      if (bilanz.fehler.length > 0) fertig(bilanz);
      return;
    }

    setStand({ fertig: 0, gesamt: gute.length });
    for (const [i, datei] of gute.entries()) {
      try {
        const { medium, neu } = await api.fotoHochladen(datei);
        // Auch ein schon vorhandenes Bild wird zugeordnet - es ist ja
        // dasselbe Foto, nur noch nicht mit dieser Person verknuepft.
        await zuordnen(medium.id);
        bilanz.ids.push(medium.id);
        if (!neu) bilanz.schonDa++;
      } catch (f) {
        bilanz.fehler.push(`${datei.name}: ${f instanceof Error ? f.message : "fehlgeschlagen"}`);
      }
      setStand({ fertig: i + 1, gesamt: gute.length });
    }
    setStand(null);
    fertig(bilanz);
  }

  // Einfuegen aus der Zwischenablage - der kurze Weg nach dem Scannen oder
  // einem Bildausschnitt. Nur wenn wirklich Dateien drin sind; getippter
  // Text soll hier nichts ausloesen.
  useEffect(() => {
    if (!aktiv) return;
    const beim = (e: ClipboardEvent) => {
      const dateien = [...(e.clipboardData?.files ?? [])];
      if (dateien.length === 0) return;
      e.preventDefault();
      void stapel(dateien);
    };
    // An `document`, nicht an `window`: in manchen lib.dom-Staenden ist
    // "paste" nur in der Document-Ereignistabelle typisiert. Das Ereignis
    // blubbert ohnehin bis hierher, das Verhalten ist dasselbe.
    document.addEventListener("paste", beim);
    return () => document.removeEventListener("paste", beim);
  });

  // Faellt ein Bild neben die Ablage, oeffnet der Browser es sonst als
  // ganze Seite - und die halb ausgefuellte Maske daneben ist weg.
  useEffect(() => {
    // Nur solange die Ablage auch wirklich da ist: sonst schluckt eine
    // unsichtbare Komponente jeden Drop auf der ganzen Seite, und eine
    // hineingezogene Datei tut gar nichts mehr - nicht einmal das, was der
    // Browser sonst von selbst taete.
    if (!aktiv) return;
    const schlucken = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", schlucken);
    window.addEventListener("drop", schlucken);
    return () => {
      window.removeEventListener("dragover", schlucken);
      window.removeEventListener("drop", schlucken);
    };
  }, [aktiv]);

  if (!aktiv) return null;

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        tiefe.current++;
        setUeber(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => {
        tiefe.current = Math.max(0, tiefe.current - 1);
        if (tiefe.current === 0) setUeber(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        tiefe.current = 0;
        setUeber(false);
        void stapel([...e.dataTransfer.files]);
      }}
      onClick={() => !laeuft && feld.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!laeuft) feld.current?.click();
        }
      }}
      className={`cursor-pointer rounded-lg border border-dashed px-4 py-5 text-center text-sm
                  transition-colors ${
                    ueber
                      ? "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200"
                      : "border-black/20 text-black/55 hover:border-black/40 dark:border-white/20 dark:text-white/55"
                  }`}
    >
      {laeuft ? (
        <span className="tabular-nums">
          lädt hoch … {stand.fertig} von {stand.gesamt}
        </span>
      ) : ueber ? (
        <span>loslassen</span>
      ) : (
        <span>
          Fotos oder Urkunden <strong className="font-medium">hierher ziehen</strong>, mit{" "}
          <kbd className="rounded border border-black/15 px-1 text-xs dark:border-white/20">
            Strg
          </kbd>
          +
          <kbd className="rounded border border-black/15 px-1 text-xs dark:border-white/20">V</kbd>{" "}
          einfügen oder klicken zum Auswählen
          <span className="mt-0.5 block text-xs text-black/40 dark:text-white/40">
            mehrere auf einmal gehen auch · Bilder und PDF
          </span>
        </span>
      )}

      <input
        ref={feld}
        type="file"
        multiple
        accept="image/*,application/pdf,.pdf"
        className="hidden"
        disabled={laeuft}
        // Der Klick, den `feld.current.click()` ausloest, steigt sonst
        // wieder bis zum umgebenden Feld auf - und das oeffnet die Auswahl
        // erneut. Endlos.
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const dateien = [...(e.target.files ?? [])];
          e.target.value = "";
          void stapel(dateien);
        }}
      />
    </div>
  );
}
